import { ArpMedia } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { FC, Suspense } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTreeState } from '../../stores/Root'
import Avatar from '../core/Avatar'
import Button from '../core/Button/Button'
import CenteredLoadingAnimation from '../core/CenteredLoadingAnimation'
import ClickToCopy from '../core/ClickToCopy'
import { Box } from '../core/Components'
import { Flex } from '../core/Flex'
import { ArrowLeft, HeartFilled, PlayIcon, ShareDarkIcon } from '../core/icons'
import SuspenseImage from '../core/SuspenseImage'
import Text from '../core/Text/Text'
import withTooltip from '../core/withTooltip'
import Gradient from '../platform/Gradient'
import useSelectedMedia from '../platform/new-explore/hooks/useSelectedMedia'

const Tooltip = withTooltip(Box, 'top')

export const FavoriteDisplay: FC<{
  favoriteCount: number
  textSize?: 12 | 16
  iconSize?: number
}> = ({ favoriteCount, textSize = 16, iconSize = 24 }) => {
  if (favoriteCount > 0) {
    return (
      <Tooltip tooltipText="Favorite count for all samples in this pack">
        <Flex css={{ alignItems: 'center', marginLeft: '25px' }}>
          <HeartFilled width={iconSize} height={iconSize} />
          <Text css={{ marginLeft: '5px' }} size={textSize}>
            {favoriteCount}
          </Text>
        </Flex>
      </Tooltip>
    )
  }

  return null
}

const SamplePackHeader: FC<{ color: string; data: any; coverArpMedia?: ArpMedia }> = ({
  color,
  data,
  coverArpMedia
}) => {
  const { globalPlayer } = useTreeState()
  const palette = ['#FAFAF6', color || '#EFF3FF']
  const navigate = useNavigate()
  const { handleSelect } = useSelectedMedia()

  const onSelect = async () => {
    await handleSelect(coverArpMedia)
  }

  return (
    <Gradient
      options={{ colorPalette: palette }}
      css={{
        height: 'auto',
        '@tablet': {
          height: '240px'
        }
      }}
    >
      <Box
        css={{
          '@tablet': {
            minHeight: '240px'
          },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}
      >
        <Box
          css={{
            paddingLeft: '40px',
            paddingTop: '15px',
            display: 'none',
            '@tablet': {
              display: 'block'
            }
          }}
        >
          <Box
            onClick={() => navigate('/home/samples')}
            css={{
              borderRadius: '50px',
              backgroundColor: '#fff',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <ArrowLeft />
          </Box>
        </Box>
        <Box
          css={{
            display: 'flex',
            padding: '24px',
            justifyContent: 'center',
            '@mobile': {
              justifyContent: 'space-between'
            },
            '@tablet': {
              width: 'calc(100% - 500px)',
              maxWidth: 'calc(100% - 500px)',
              padding: '0 0 30px 40px'
            }
          }}
        >
          <Box css={{ width: '100%' }}>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Flex
                css={{
                  width: '100%',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  '@laptop15inch': {
                    flexDirection: 'row'
                  }
                }}
              >
                <Flex css={{ gap: '24px' }}>
                  <Suspense fallback={<CenteredLoadingAnimation light />}>
                    <Avatar
                      imageUrl={data.user.profilePicture?.url}
                      alt={data.user.profilePicture?.name}
                      bg={data.user.profilePicture ? 'none' : 'black'}
                      css={{
                        height: '84px',
                        width: '84px',
                        borderRadius: '50px'
                      }}
                    />
                  </Suspense>
                  <Box>
                    <Text
                      css={{
                        fontSize: '32px',
                        lineHeight: '40px',
                        '@laptop': {
                          fontSize: '40px',
                          lineHeight: '50px'
                        }
                      }}
                      fontWeight="bold"
                    >
                      {data.title}
                    </Text>
                    <Link to={`/profile/${data.user.walletAddress}`}>
                      <Text
                        css={{ color: 'rgba(20, 20, 20, 0.75)' }}
                        size={24}
                        fontWeight="semibold"
                      >
                        {data.user.displayName}
                      </Text>
                    </Link>
                  </Box>
                </Flex>
                <Box
                  css={{
                    display: 'none',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    minWidth: '230px',
                    marginTop: '24px',
                    '@tablet': {
                      display: 'flex'
                    }
                  }}
                >
                  <ClickToCopy copyContent={window.location.href}>
                    <ShareDarkIcon />
                  </ClickToCopy>

                  <FavoriteDisplay favoriteCount={data.sampleFavoriteCount} />
                  {data.coverArpMediaId && (
                    <Button
                      onClick={onSelect}
                      css={{
                        backgroundColor: 'rgba(20, 20, 20, 0.8)',
                        color: '#fff',
                        width: '110px',
                        height: '40px',
                        marginLeft: '30px',
                        borderRadius: '50px'
                      }}
                    >
                      <PlayIcon fill="#fff" />
                      <span style={{ marginLeft: '10px' }}>Play demo</span>
                    </Button>
                  )}
                </Box>
              </Flex>
            </Box>
            <Box css={{ display: 'flex', justifyContent: 'center' }}>
              <Suspense fallback={<CenteredLoadingAnimation light />}>
                <SuspenseImage
                  src={data.coverImage?.url}
                  alt={data.coverImage?.name}
                  css={{
                    height: 'inherit',
                    width: '100%',
                    marginTop: '-60px',
                    borderRadius: '12px',
                    transform: 'translate(0, 100px)',
                    '@mobile': {
                      maxWidth: '550px'
                    },
                    '@tablet': {
                      display: 'none'
                    }
                  }}
                />
              </Suspense>
            </Box>
          </Box>
        </Box>
      </Box>
    </Gradient>
  )
}

export default observer(SamplePackHeader)
